import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LoanOfficerAd {
  id: string;
  first_name: string;
  last_name: string;
  license_number: string | null;
  ad_image_url: string | null;
}

/**
 * Fetches the loan officer ad for a given site based on town coverage.
 * Returns the first active loan officer with an ad image that covers towns in the site.
 */
export function useLoanOfficerAd(siteId: string | null, townIds: string[] = []) {
  return useQuery({
    queryKey: ['loan-officer-ad', siteId, townIds],
    queryFn: async (): Promise<LoanOfficerAd | null> => {
      if (!siteId || townIds.length === 0) return null;

      // Get loan officers who have coverage for these towns and have an ad image
      const { data: coverageData } = await supabase
        .from('loan_officer_coverage')
        .select('loan_officer_id')
        .in('town_id', townIds);

      if (!coverageData || coverageData.length === 0) return null;

      const officerIds = [...new Set(coverageData.map(c => c.loan_officer_id))];

      // Get the first active loan officer with an ad image
      const { data: officers } = await supabase
        .from('loan_officers')
        .select('id, first_name, last_name, license_number, ad_image_url')
        .in('id', officerIds)
        .eq('is_active', true)
        .not('ad_image_url', 'is', null)
        .limit(1);

      if (!officers || officers.length === 0) return null;

      return officers[0];
    },
    enabled: !!siteId && townIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Fetches the loan officer ad for a specific town.
 */
export function useLoanOfficerAdForTown(townId: string | null) {
  return useQuery({
    queryKey: ['loan-officer-ad-town', townId],
    queryFn: async (): Promise<LoanOfficerAd | null> => {
      if (!townId) return null;

      // Get loan officers who have coverage for this town
      const { data: coverageData } = await supabase
        .from('loan_officer_coverage')
        .select('loan_officer_id')
        .eq('town_id', townId);

      if (!coverageData || coverageData.length === 0) return null;

      const officerIds = [...new Set(coverageData.map(c => c.loan_officer_id))];

      // Get the first active loan officer with an ad image
      const { data: officers } = await supabase
        .from('loan_officers')
        .select('id, first_name, last_name, license_number, ad_image_url')
        .in('id', officerIds)
        .eq('is_active', true)
        .not('ad_image_url', 'is', null)
        .limit(1);

      if (!officers || officers.length === 0) return null;

      return officers[0];
    },
    enabled: !!townId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetches the loan officer ad for a specific area.
 */
export function useLoanOfficerAdForArea(areaId: string | null, townId: string | null) {
  return useQuery({
    queryKey: ['loan-officer-ad-area', areaId, townId],
    queryFn: async (): Promise<LoanOfficerAd | null> => {
      if (!areaId && !townId) return null;

      // Get loan officers who have coverage for this area OR its parent town
      const coverageQueries = [];
      
      if (areaId) {
        const { data: areaCoverage } = await supabase
          .from('loan_officer_coverage')
          .select('loan_officer_id')
          .eq('area_id', areaId);
        if (areaCoverage) coverageQueries.push(...areaCoverage);
      }
      
      if (townId) {
        const { data: townCoverage } = await supabase
          .from('loan_officer_coverage')
          .select('loan_officer_id')
          .eq('town_id', townId);
        if (townCoverage) coverageQueries.push(...townCoverage);
      }

      if (coverageQueries.length === 0) return null;

      const officerIds = [...new Set(coverageQueries.map(c => c.loan_officer_id))];

      // Get the first active loan officer with an ad image
      const { data: officers } = await supabase
        .from('loan_officers')
        .select('id, first_name, last_name, license_number, ad_image_url')
        .in('id', officerIds)
        .eq('is_active', true)
        .not('ad_image_url', 'is', null)
        .limit(1);

      if (!officers || officers.length === 0) return null;

      return officers[0];
    },
    enabled: !!(areaId || townId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
